import React, { useEffect, useState } from 'react';
import { Text, TextInput, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { OperationalChipSelectRow } from '../../shared/filters/OperationalChipSelectRow';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormSection } from '../../shared/modals/ModalFormSection'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { formatUnitLabel } from '../../catalog/domain-api';
import { formatMoney } from '../../shared/format/public-api';
import { createTodayDate, formatIsoDate } from '@shared/vueltoDateTime';
import type { ProductVariant } from '../../catalog/domain-api';
import {
  normalizeStockUnitOfMeasure,
  stockQuantityAllowsFraction,
} from '../application/inventoryUtils';
import { addStockModalStyles as styles } from './addStockModal.styles';
import type { AddStockModalProps } from './addStockModal.types';

type Props = Pick<
  AddStockModalProps,
  | 'entryQuantity'
  | 'entryUnitCost'
  | 'entryReason'
  | 'entryMinQuantity'
  | 'entryExpiry'
  | 'entrySupplierId'
  | 'entryLotNumber'
  | 'entryVariantId'
  | 'lastUnitCost'
  | 'selectedProduct'
  | 'onEntryQuantityChange'
  | 'onEntryUnitCostChange'
  | 'onEntryReasonChange'
  | 'onEntryMinQuantityChange'
  | 'onEntryExpiryChange'
  | 'onEntryLotNumberChange'
  | 'onEntryVariantIdChange'
> & {
  supplierLabel: string;
  onOpenSupplierPicker: () => void;
  availableVariants: ProductVariant[];
  /** Capability purchases: «Compra» redirige al módulo — el chip lo avisa. */
  purchasesEnabled?: boolean;
};

function variantLabel(v: ProductVariant): string {
  const suffix = v.nameSuffix?.trim();
  if (suffix) return suffix;
  const attrs = Object.entries(v.attributes).map(([k, val]) => `${k}: ${val}`).join(' · ');
  return attrs || 'Variante';
}

export const AddStockModalEntryFields = ({
  entryQuantity,
  entryUnitCost,
  entryReason,
  entryMinQuantity,
  entryExpiry,
  entrySupplierId,
  entryLotNumber,
  entryVariantId,
  lastUnitCost,
  selectedProduct,
  onEntryQuantityChange,
  onEntryUnitCostChange,
  onEntryReasonChange,
  onEntryMinQuantityChange,
  onEntryExpiryChange,
  onEntryLotNumberChange,
  onEntryVariantIdChange,
  supplierLabel,
  onOpenSupplierPicker,
  availableVariants,
  purchasesEnabled = false,
}: Props) => {
  const allowsFraction = stockQuantityAllowsFraction(selectedProduct?.unitOfMeasure);
  const unitLabel = formatUnitLabel(normalizeStockUnitOfMeasure(selectedProduct?.unitOfMeasure));
  // ISO 'YYYY-MM-DD' compara lexicográficamente; ambas en calendario local.
  const todayIso = formatIsoDate(createTodayDate());
  const entryExpiryIsPast =
    /^\d{4}-\d{2}-\d{2}$/.test(entryExpiry.trim()) && entryExpiry.trim() < todayIso;

  // Desktop (sheet centrada ~860px): campos en 2 columnas. Mobile: apilado.
  const { width: winW } = useWindowDimensions();
  const isWide = winW >= WEB_SIDEBAR_BREAKPOINT;
  const rowStyle = isWide ? styles.fieldRow : undefined;
  const colStyle = isWide ? styles.fieldCol : undefined;

  const hasExpiryDraft =
    entryExpiry.trim().length > 0 || entryLotNumber.trim().length > 0;
  const [showExpiryFields, setShowExpiryFields] = useState(hasExpiryDraft);

  useEffect(() => {
    if (hasExpiryDraft) {
      setShowExpiryFields(true);
    }
  }, [hasExpiryDraft]);

  return (
  <>
    <ModalFormSection title="Ingreso" divided>
      <View style={rowStyle}>
        <ModalFormField
          label={allowsFraction ? `Cantidad a ingresar (${unitLabel})` : 'Cantidad a ingresar'}
          required
          style={colStyle}
        >
          <NumericTextInput
            kind={allowsFraction ? 'decimalMoney' : 'integer'}
            style={[modalExperience.formInput, styles.quantityInput]}
            value={entryQuantity}
            onChangeText={onEntryQuantityChange}
            placeholder={allowsFraction ? 'Ej: 1,5' : 'Ej: 24'}
            placeholderTextColor={ticketColors.ink3}
          />
        </ModalFormField>
        <ModalFormField label="Costo unitario" required style={colStyle}>
          <NumericTextInput
            kind="decimalMoney"
            style={modalExperience.formInput}
            value={entryUnitCost}
            onChangeText={onEntryUnitCostChange}
            placeholder="Ej: 120 o 120,50"
            placeholderTextColor={ticketColors.ink3}
          />
          {entryReason === 'purchase_reception' && lastUnitCost != null ? (
            <Text style={styles.lastCostHint}>Último: {formatMoney(lastUnitCost)}</Text>
          ) : null}
        </ModalFormField>
      </View>
      <ModalFormField label="Motivo">
        <OperationalChipSelectRow
          ticketTone
          value={entryReason}
          onChange={onEntryReasonChange}
          options={[
            // «Compra ›» con purchases ON sale del modal → el › lo signpostea.
            { value: 'purchase_reception', label: purchasesEnabled ? 'Compra ›' : 'Compra' },
            { value: 'stock_restock', label: 'Reposición' },
            { value: 'stock_initial_load', label: 'Carga inicial' },
          ]}
        />
        {purchasesEnabled ? (
          <Text style={modalExperience.formSectionHelper}>
            Una compra se carga en Compras, con proveedor e ítems.
          </Text>
        ) : null}
      </ModalFormField>
    </ModalFormSection>

    <ModalFormSection title="Detalle" divided>
      <View style={rowStyle}>
        {entryReason === 'purchase_reception' ? (
          <ModalFormField label="Proveedor" style={colStyle}>
            <TouchableOpacity
              style={[
                modalExperience.entitySelector,
                entrySupplierId != null ? modalExperience.entitySelectorActive : null,
              ]}
              onPress={onOpenSupplierPicker}
              accessibilityRole="button"
              accessibilityLabel={`Proveedor: ${supplierLabel}. Abrir lista`}
            >
              <Text
                style={[
                  styles.supplierValue,
                  entrySupplierId == null && styles.supplierValueMuted,
                ]}
                numberOfLines={1}
              >
                {supplierLabel}
              </Text>
              <Ionicons name="chevron-down" size={18} color={ticketColors.ink2} />
            </TouchableOpacity>
          </ModalFormField>
        ) : null}

        {availableVariants.length > 0 ? (
          <ModalFormField label="Variante" style={colStyle}>
            <OperationalChipSelectRow
              ticketTone
              value={entryVariantId ?? ''}
              onChange={(v) => onEntryVariantIdChange(v === '' ? null : v)}
              options={[
                { value: '', label: 'Sin variante (parent)' },
                ...availableVariants.map((v) => ({ value: v.id, label: variantLabel(v) })),
              ]}
            />
          </ModalFormField>
        ) : null}
      </View>

      {!showExpiryFields ? (
        <TouchableOpacity
          onPress={() => setShowExpiryFields(true)}
          accessibilityRole="button"
          accessibilityLabel="Tiene vencimiento o número de lote"
          testID="stock.add.expand-expiry"
        >
          <Text style={modalExperience.formSectionHelper}>Tiene vencimiento o n° de lote</Text>
        </TouchableOpacity>
      ) : (
        <View style={rowStyle}>
          <ModalFormField label="Vencimiento" style={colStyle}>
            <InventoryIsoDateField
              value={entryExpiry}
              onChange={onEntryExpiryChange}
              emptyLabel="Sin vencimiento"
              ariaLabel="Fecha de vencimiento"
              clearButtonLabel="Quitar vencimiento"
              iosEmptyBehavior="seedToday"
              minToday
            />
            {entryExpiryIsPast ? (
              <Text style={styles.expiryPastWarning}>
                La fecha ya pasó — el lote va a quedar como vencido
              </Text>
            ) : null}
          </ModalFormField>

          {entryReason === 'purchase_reception' ? (
            <ModalFormField label="N° de lote" style={colStyle}>
              <TextInput
                style={modalExperience.formInput}
                value={entryLotNumber}
                onChangeText={onEntryLotNumberChange}
                placeholder="Ej: L-2024-001"
                placeholderTextColor={ticketColors.ink3}
              />
            </ModalFormField>
          ) : null}
        </View>
      )}

      <ModalFormField
        label={allowsFraction ? `Stock mínimo del producto (${unitLabel})` : 'Stock mínimo del producto'}
      >
        <NumericTextInput
          kind={allowsFraction ? 'decimalMoney' : 'integer'}
          style={modalExperience.formInput}
          value={entryMinQuantity}
          onChangeText={onEntryMinQuantityChange}
          placeholder={allowsFraction ? 'Ej: 0,5' : 'Ej: 6'}
          placeholderTextColor={ticketColors.ink3}
        />
      </ModalFormField>
    </ModalFormSection>
  </>
  );
};
